/**
 * Product-UI language resolution (ENG-MULTILOCALE-FIRST-CLASS Track I).
 *
 * Contract: docs/product/experience-and-ui.md § Settings → UI language —
 * default is the browser language resolved against the available catalog
 * set, falling back to English; the stored per-user preference wins.
 * Pure module (no i18next import) so the rule is unit-testable and the
 * catalog set stays declared in exactly one place.
 */

export const UI_LANGUAGES = ["en", "es-419", "de-DE"] as const;

export type UiLanguage = (typeof UI_LANGUAGES)[number];

/** Native-name labels rendered by the /settings/account selector. */
export const UI_LANGUAGE_LABELS: Record<UiLanguage, string> = {
  en: "English",
  "es-419": "Español (Latinoamérica)",
  "de-DE": "Deutsch",
};

/**
 * Primary-subtag → catalog mapping for browser languages that don't name
 * a catalog exactly (`es-MX` → `es-419`, `de-AT` → `de-DE`). Regional
 * variants of a shipped base language read the shipped catalog rather
 * than falling straight to English.
 */
const PRIMARY_SUBTAG_TO_CATALOG: Record<string, UiLanguage> = {
  en: "en",
  es: "es-419",
  de: "de-DE",
};

function isUiLanguage(value: string): value is UiLanguage {
  return (UI_LANGUAGES as readonly string[]).includes(value);
}

/**
 * The UI languages actually OFFERABLE right now — English plus every other
 * shipped catalog that has content. The es-419 / de-DE catalogs are
 * LLM-generated at build time and committed empty (`{}`) until that step
 * runs (ENG-MULTILOCALE-FIRST-CLASS I3), so an empty catalog would render
 * English behind a Spanish/German label — a confusing switcher option.
 * Gating on catalog content means the language auto-appears the instant its
 * catalog is populated, with no code change. English is always available
 * (it is the base catalog and MUST stay complete).
 */
export function availableUiLanguages(
  catalogs: Partial<Record<UiLanguage, Readonly<Record<string, unknown>>>>,
): UiLanguage[] {
  return UI_LANGUAGES.filter(
    (lang) => lang === "en" || Object.keys(catalogs[lang] ?? {}).length > 0,
  );
}

/**
 * Resolve the UI language from the stored preference and the browser's
 * language list (``navigator.languages``). Stored preference wins when it
 * names an AVAILABLE catalog; otherwise the first browser language that
 * resolves (exact tag, then primary subtag) to an available catalog wins;
 * otherwise English. ``available`` defaults to the full shipped set so the
 * pure rule stays testable without catalog content; callers that know which
 * catalogs are populated (``i18n/index.ts``) pass the narrowed set so a
 * browser-default to an unpopulated catalog degrades to English rather than
 * silently selecting a locale that only renders English.
 */
export function resolveUiLanguage(
  stored: string | null,
  browserLanguages: readonly string[],
  available: readonly UiLanguage[] = UI_LANGUAGES,
): UiLanguage {
  const offerable = (lang: UiLanguage): boolean => available.includes(lang);
  const fallback: UiLanguage = offerable("en") ? "en" : (available[0] ?? "en");
  if (stored !== null && isUiLanguage(stored) && offerable(stored))
    return stored;
  for (const raw of browserLanguages) {
    if (!raw) continue;
    const tag = raw.trim();
    const exact = UI_LANGUAGES.find(
      (l) => l.toLowerCase() === tag.toLowerCase(),
    );
    if (exact && offerable(exact)) return exact;
    const primary = tag.split("-")[0].toLowerCase();
    const mapped = PRIMARY_SUBTAG_TO_CATALOG[primary];
    if (mapped && offerable(mapped)) return mapped;
  }
  return fallback;
}
